import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import logo from '../../../assets/images/bombiix-logo.svg';
import { getMFAQrCode, verifyOtp, enableMfa, SetupSecurityQuestions } from '../store/Auth.action';
import { clearSetupToken, hasSetupToken } from '../../../services/auth.service';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import './Setup2FA.css';

function Setup2FA() {
  const { t } = useTranslation('common');
  const searchParams = new URLSearchParams(window?.location?.search);
  let emailId = searchParams.get('email');
  if (emailId) emailId = decodeURIComponent(emailId);
  const userId = searchParams.get('userId') || '';
  const [errors, setErrors] = useState({ code: '', isError: false });
  const history = useHistory();
  const dispatch = useDispatch();
  const [verificationCode, setVerificationCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [authyAppSelected, setAuthyAppSelected] = useState(true);
  const [emailSelected, setEmailSelected] = useState(false);
  const [getMfaEnabled, setGetMfaEnabled] = useState(false);
  const [activeUnderline, setActiveUnderline] = useState({});
  const switchTabRef = useRef(null);

  // Security Questions state
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [questionErrors, setQuestionErrors] = useState({});
  const [showQuestionsStep, setShowQuestionsStep] = useState(false);

  const QuestionList = [
    t('QUSTIONS.QUS.MOTHER_MAIDEN_NAME'),
    t('QUSTIONS.QUS.FIRST_PET_NAME'),
    t('QUSTIONS.QUS.CITY_WHERE_U_BORN'),
    t('QUSTIONS.QUS.CHILD_HOOD_NICKNAME'),
    t('QUSTIONS.QUS.FIRST_VACATION'),
    t('QUSTIONS.QUS.FAVORITE_TEACHER'),
    t('QUSTIONS.QUS.FAVORITE_CARTOON_CHARACTER'),
  ];

  const getQRcode = async () => {
    const user = {
      email: encodeURIComponent(emailId),
      userId,
    };

    const res = await dispatch(getMFAQrCode(user));

    if (res?.url) {
      // Delayed rendering to stabilize DOM layout (helps iPhone)
      setTimeout(() => {
        const encoded = encodeURIComponent(res.url);
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encoded}`;
        setQrCodeUrl(qrUrl);
      }, 300); // 300ms delay
    }

    if (res?.mfaEnabled) setGetMfaEnabled(res.mfaEnabled);
    if (res?.mfaEnabled && !res?.verificationQuestion) setShowQuestionsStep(true);
  };

  /**
   * Setup runs before a session exists and is authorised by a short-lived token
   * issued when the invite link was redeemed or the password was accepted at
   * login. Landing here without one (invite link already used, or the tab was
   * closed and reopened) is a dead end, so send the user to sign in — that mints
   * a fresh token and returns them here automatically.
   */
  useEffect(() => {
    if (!hasSetupToken()) {
      toasterMsg(t("SETUP_2FA.SESSION_EXPIRED"), "info", 6000);
      history.push("/login");
      return;
    }
    getQRcode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const calculateUnderlinePosition = () => {
      if (switchTabRef.current) {
        const activeTab = switchTabRef.current.querySelector('.active');
        if (activeTab) {
          const tabRect = activeTab.getBoundingClientRect();
          const parentRect = switchTabRef.current.getBoundingClientRect();

          setActiveUnderline({
            width: tabRect.width,
            left: tabRect.left - parentRect.left,
          });
        } else {
          setActiveUnderline({
            width: 0,
            display: 'none',
          });
        }
      }
    };

    // Use setTimeout to ensure DOM is fully rendered and styles are applied
    const timeoutId = setTimeout(calculateUnderlinePosition, 100);

    // Add resize observer to recalculate on layout changes
    const resizeObserver = new ResizeObserver(() => {
      calculateUnderlinePosition();
    });

    if (switchTabRef.current) {
      resizeObserver.observe(switchTabRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [authyAppSelected, emailSelected]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (authyAppSelected) {
        if (!verificationCode.trim()) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            code: t('SETUP_2FA.CODE_REQUIRED'),
            isError: true,
          }));
        } else {
          const data = {
            code: verificationCode.trim(),
            email: emailId,
            userId,
          };
          const result = await dispatch(verifyOtp(data, t));
          if (result?.errMsg) {
            setGetMfaEnabled(false);
          } else if (!result?.verificationQuestion) {
            setShowQuestionsStep(true);
          } else {
            clearSetupToken();
            history.push('/login');
          }
        }
      }
    },
    [authyAppSelected, dispatch, emailId, history, t, userId, verificationCode],
  );

  const handleSwitchButton = (type) => {
    if (type === 'authenticator_app') {
      setAuthyAppSelected(true);
      setEmailSelected(false);
    } else {
      setAuthyAppSelected(false);
      setEmailSelected(true);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (emailSelected) {
      // Validate security questions first
      if (selectedQuestions.length < 2 || Object.values(questionErrors).length !== 0) {
        return;
      }

      const mfaData = {
        mfaType: 'email',
        email: emailId,
        userId,
      };
      const mfaResponse = await dispatch(enableMfa(mfaData, t));
      if (!mfaResponse?.mfaEnabled) {
        return;
      }

      const questionsData = {
        questions: selectedQuestions,
        email: encodeURIComponent(emailId),
        userId,
      };
      const questionsResponse = await dispatch(SetupSecurityQuestions(questionsData, t));

      if (questionsResponse) {
        clearSetupToken();
        history.push('/login');
      }
    }
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setVerificationCode(value);
  };

  // Security Questions helper functions
  const checkQuestionErrors = (index, name, value) => {
    const newArray = [...selectedQuestions];
    newArray[index] = newArray[index] ? { ...newArray[index], [name]: value } : { [name]: value };
    newArray.forEach((ques, idx) => {
      if (ques) {
        if (!ques?.answer) {
          setQuestionErrors((prev) => ({ ...prev, [idx]: t('QUSTIONS.QUS.ANSWER_REQUIRED_ERROR') }));
        } else if (!ques?.question || ques?.question === 'default') {
          setQuestionErrors((prev) => ({ ...prev, [idx]: t('QUSTIONS.QUS.SELECT_QUESTIONS_ERROR') }));
        } else {
          setQuestionErrors((prev) => {
            const newError = { ...prev };
            delete newError[index];
            return newError;
          });
        }
      }
    });
  };

  const handleQuestionInput = (e, index, name = null) => {
    let fieldName;
    let fieldValue;

    if (name) {
      // This is from react-select
      fieldName = name;
      fieldValue = e ? e.value : '';
    } else {
      // This is from regular input
      fieldName = e.target.name;
      fieldValue = e.target.value.trim();
    }

    setSelectedQuestions((prev) => {
      const newArray = [...prev];
      newArray[index] = { ...newArray[index], [fieldName]: fieldValue };
      return newArray;
    });

    checkQuestionErrors(index, fieldName, fieldValue);
  };

  const checkIfAlreadySelected = (id) => {
    const selectedQuesList = selectedQuestions.map((opt) => opt?.question || null).filter((opt) => opt !== null);
    return selectedQuesList.includes(id);
  };

  const getQuestionOptions = () => {
    return QuestionList.map((question) => ({
      label: question,
      value: question,
      isDisabled: checkIfAlreadySelected(question),
    }));
  };

  const getSelectedQuestionValue = (index) => {
    const selectedQuestion = selectedQuestions[index]?.question;
    if (selectedQuestion && selectedQuestion !== 'default') {
      return { label: selectedQuestion, value: selectedQuestion };
    }
    return null;
  };

  const formControl = (index) => {
    return (
      <Row key={index}>
        <Col className="mb-1">
          <Form.Group>
            <div className="common-react-select mb-2">
              <Select
                options={getQuestionOptions()}
                value={getSelectedQuestionValue(index)}
                onChange={(selectedOption) => handleQuestionInput(selectedOption, index, 'question')}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                className="common-select"
                classNamePrefix="common-select"
                placeholder={t('QUSTIONS.QUS.SELECT_QUESTION')}
                isClearable
                isOptionDisabled={(option) => option.isDisabled}
              />
            </div>
            <Form.Control type="text" name="answer" onChange={(e) => handleQuestionInput(e, index)} placeholder={t('QUSTIONS.QUS.ANSWER_PLACEHOLDER')} />
            {isSubmitted && <Form.Text className="text-danger">{questionErrors[index]}</Form.Text>}
            {isSubmitted && !questionErrors[index] && !selectedQuestions[index] && (
              <Form.Text className="text-danger">{t('QUSTIONS.QUS.SELECT_QUESTIONS_ERROR')}</Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
    );
  };

  const renderSecurityQuestionsForm = (onSubmit) => (
    <div className="mt-2">
      <div className="security-questions-section mt-3">
        <p className="text-center mb-2 text-medium">{t('QUSTIONS.QUS.TITLE')}</p>
        <Form onSubmit={onSubmit}>
          {formControl(0)}
          {formControl(1)}
          <div className="d-flex justify-content-center align-items-center">
            <Button type="submit" className="btn-theme">
              {t('SETUP_2FA.SETUP_COMPLETE')}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );

  const handleSecurityQuestionsStep = async (event) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (selectedQuestions.length < 2 || Object.values(questionErrors).length !== 0) {
      return;
    }
    const questionsData = {
      questions: selectedQuestions,
      email: encodeURIComponent(emailId),
      userId,
    };
    const questionsResponse = await dispatch(SetupSecurityQuestions(questionsData, t));
    if (questionsResponse) {
      clearSetupToken();
      history.push('/login');
    }
  };

  return (
    <Container>
      <div className="login-container-bombiix">
        <div className="logo-container text-center">
          <img src={logo} className="img-fluid logo-image" alt="logo" />
        </div>
        <p className="text-medium text-center mt-40 mb-0">{t('SETUP_2FA.TWO_FACTOR_AUTHENTICATION')}</p>
        {!showQuestionsStep && (
          <div className="common-switch-container mt-2 mb-2 margin-auto justify-content-center">
            <ul className="new-switch" ref={switchTabRef}>
              <li className={authyAppSelected ? 'active' : ''}>
                <button type="button" className="switch-item" onClick={() => handleSwitchButton('authenticator_app')}>
                  {t('SETUP_2FA.AUTHENTICATOR')}
                </button>
              </li>
              <li className={emailSelected ? 'active' : ''}>
                <button type="button" className="switch-item" onClick={() => handleSwitchButton('email')}>
                  {t('SETUP_2FA.EMAIL')}
                </button>
              </li>
            </ul>
            <div className="tab-underline" style={activeUnderline} />
          </div>
        )}

        {authyAppSelected && !showQuestionsStep && (
          <div className="setup-content-container">
            {qrCodeUrl && !getMfaEnabled && (
              <>
                {/* <h3 className="authy-title">{t("SETUP_2FA.SETUP_AUTHENTICATION")}</h3> */}
                <p className="text-medium text-center mb-2">{t('SETUP_2FA.SCAN_APP')}</p>
                <div className="qr-container">
                  <img src={qrCodeUrl} alt="qr-code" key={qrCodeUrl} />
                </div>

                <Form className="w-100" onSubmit={handleSubmit}>
                  <Form.Group controlId="formBasicEmail">
                    <Form.Label>
                      {t('SETUP_2FA.VERIFICATION')}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      key="verifyCode"
                      id="verifyCode"
                      name="verifyCode"
                      placeholder={t('SETUP_2FA.ENTER_CODE_PLACEHOLDER')}
                      className={verificationCode && 'data-filled'}
                      value={verificationCode}
                      onChange={handleInputChange}
                    />

                    {errors?.isError && <div className="text-danger err-text">{errors?.code}</div>}
                  </Form.Group>
                  <div className="d-flex justify-content-center align-items-center">
                    <Button variant="primary" className="btn-theme" type="submit">
                      {t('SETUP_2FA.SUBMIT')}
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </div>
        )}
        {emailSelected && !getMfaEnabled && renderSecurityQuestionsForm(handleMfa)}
        {getMfaEnabled && !showQuestionsStep && (
          <div className="mt-2">
            <h3 className="mb-2 enable-text">{t('SETUP_2FA.2FA_ACCOUNT')}</h3>
            <Button onClick={() => history.push('/login')} variant="primary" className="btn-default mt-2" type="submit">
              {t('SETUP_2FA.NEXT')}
            </Button>
          </div>
        )}
        {showQuestionsStep && renderSecurityQuestionsForm(handleSecurityQuestionsStep)}
      </div>
    </Container>
  );
}

export default Setup2FA;
